/**
 * Sources panel — the retrieved documents an agent reply was grounded in.
 *
 * Rendered under an assistant bubble when the BFF surfaced grounding sources
 * (agent backend only — see `agent-api.ts` `onSources`). Each source shows
 * its filename · page · relevance, a short quote, and — when the hit resolves
 * to a stored blob (tier + path) — a download button that streams the file
 * from the BFF (`downloadDoc`, same path the Documents panel uses, so the
 * caller's role boundary is enforced server-side).
 */

import { useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { downloadDoc } from "@/lib/docs-api";
import type { AgentSource } from "../agent-api";

/** Trigger a browser save for fetched bytes (mirrors DocumentsPanel). */
function saveBlobAs(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SourceCard({ source }: { source: AgentSource }) {
  const [busy, setBusy] = useState(false);
  const pageLabel =
    source.page !== "" && source.page !== "None" && source.page != null
      ? `p.${source.page}`
      : null;

  async function onDownload() {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await downloadDoc(source.tier, source.path);
      saveBlobAs(blob, source.filename || source.path.split("/").pop() || "document");
    } catch (err) {
      // 403 (lost access), 404 (deleted since retrieval), or network — the
      // BFF is the authz boundary, so a refusal here is correct, not a bug.
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="border-border/40 bg-secondary/30 rounded-md border p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <FileText className="text-muted-foreground h-3 w-3 shrink-0" aria-hidden />
          <span className="text-foreground/90 truncate font-mono text-fs-label">
            {source.filename || "source"}
          </span>
          {pageLabel && (
            <span className="text-muted-foreground shrink-0 text-fs-label">{pageLabel}</span>
          )}
        </div>
        {source.downloadable && (
          <button
            type="button"
            onClick={onDownload}
            disabled={busy}
            aria-label={`Download ${source.filename || "source"}`}
            className={cn(
              "text-true-blue inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5",
              "text-fs-label hover:underline disabled:opacity-50",
            )}
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Download className="h-3 w-3" aria-hidden />
            )}
            Download
          </button>
        )}
      </div>
      {source.quote && (
        <p className="text-muted-foreground mt-1 line-clamp-3 text-fs-label leading-snug italic">
          “{source.quote}”
        </p>
      )}
    </li>
  );
}

/**
 * Sources panel with two states (ADO #460):
 *
 *   - The agent cited ≥1 retrieved passage (`citedIndices` non-empty) → show
 *     only those, headed "Sources" — these documents demonstrably informed
 *     the answer.
 *   - The agent cited none → the retrieval still ran, but we can't claim it
 *     was used, so we show every hit under a de-emphasised, collapsed
 *     "Retrieved context" disclosure with neutral framing.
 *
 * `citedIndices` is the resolved set of `[n]` markers found in the finished
 * reply (see use-chat). It maps to `AgentSource.index`, NOT array position.
 */
export function Sources({
  sources,
  citedIndices,
  grounding,
}: {
  sources: AgentSource[];
  citedIndices: number[];
  grounding?: boolean;
}) {
  if (!sources || sources.length === 0) {
    if (!grounding) return null;
    return (
      <div className="mt-2">
        <p className="text-muted-foreground/70 font-mono text-fs-micro tracking-[0.16em] uppercase">
          No matching documents found
        </p>
      </div>
    );
  }

  const cited = new Set(citedIndices);
  const hasCitations = cited.size > 0;

  if (hasCitations) {
    const shown = sources.filter((s) => cited.has(s.index));
    // Defensive: if every cited index somehow failed to resolve, fall through
    // to the retrieved-context view rather than render an empty "Sources".
    if (shown.length > 0) {
      return (
        <div className="mt-2">
          <div className="text-muted-foreground mb-1 font-mono text-fs-micro tracking-[0.16em] uppercase">
            Sources ({shown.length})
          </div>
          <ul className="flex flex-col gap-1">
            {shown.map((s) => (
              <SourceCard key={`${s.tier}-${s.path}-${s.index}`} source={s} />
            ))}
          </ul>
        </div>
      );
    }
  }

  // Fallback: retrieval ran but the agent cited nothing. Don't imply use —
  // present these as "what we searched", collapsed by default.
  return (
    <details className="mt-2">
      <summary className="text-muted-foreground/70 hover:text-muted-foreground cursor-pointer font-mono text-fs-micro tracking-[0.16em] uppercase select-none">
        Retrieved context ({sources.length})
      </summary>
      <ul className="mt-1 flex flex-col gap-1">
        {sources.map((s) => (
          <SourceCard key={`${s.tier}-${s.path}-${s.index}`} source={s} />
        ))}
      </ul>
    </details>
  );
}
